import type { BlobArrayType } from "@pdfking/blob"
import { BlobArray, BlobToUint8 } from "@pdfking/blob"
import { AutoThrow } from "@pdfking/errors"
import type { StreamInput } from "@pdfking/io"
import { Max, Min, ToNumber, POS_INFINITY } from "@pdfking/math"
import { MERGE_TRAILER_LENGTH } from "@pdfking/pdfium"
import type {
  GetBlockPointer,
  IsDataAvailPointer,
  PageNumber,
  PdfReaderPointer,
  PdfiumAPI,
} from "@pdfking/pdfium"
import type { PDFPageMeta } from "@pdfking/renderer-api"
import { NullQ } from "@pdfking/structs"
import type { Bytes } from "@pdfking/structs"
import type { Sequence } from "@pdfking/tfunc"

import { PageRange } from "./PageRange"
import type { StreamBufferType } from "./StreamBuffer"
import { StreamBuffer } from "./StreamBuffer"
import {
  BlankMarker,
  INTEGRITY_LENGTH,
  ParseIntegrity,
  ParseSectionMeta,
  ParseSectionPrefix,
  ParseSharedPrefix,
  SECTION_PREFIX,
} from "./StreamFormat"

export type ReadPage<T> = (
  handle: PdfReaderPointer,
  page_index: PageNumber,
  page: number
) => Promise<T>

// A page of our merge output as written: its section is a whole pdf already,
// and its meta holds what split would have read out of it.
export type ReadSection<T> = (bytes: BlobArrayType, meta: PDFPageMeta, page: number) => T

export type ReadPagesOptions<T> = {
  // our merge output hands its sections here as they are, unopened
  section?: ReadSection<T>
  // our merge output read by pdfium whole, as the plain pdf it also is
  skip_pdfking?: boolean
  // how much more a pull brings in (see StreamBuffer)
  chunk_size: Bytes
}

// pdfium over bytes held as views: every block it reads is written from the
// parts it spans straight into wasm memory, the one copy crossing the
// boundary takes. `bytes` is asked on every read, so they may still be
// arriving; the file is `size` long. `complete` bytes load as from memory,
// with the repairs a damaged file needs; bytes still arriving load through
// the availability api, which reads a page as soon as it is in.
const OpenPdfium = async (
  api: PdfiumAPI,
  bytes: BlobArrayType,
  size: number,
  complete: boolean
): Promise<{ handle: PdfReaderPointer; close: () => Promise<void> }> => {
  const { instance } = api

  const getBlock = (
    _param: number | bigint,
    position: number | bigint,
    bufPtr: number | bigint,
    sz: number | bigint
  ): number => {
    const start = ToNumber(position)
    const end = start + ToNumber(sz)
    if (end > bytes.size) return 0
    let at = ToNumber(bufPtr)
    for (const part of bytes.slice(start, end).parts) {
      instance.write(at, part as Uint8Array)
      at += (part as Uint8Array).length
    }
    return 1
  }

  const isDataAvail = (
    _pThis: number | bigint,
    offset: number | bigint,
    sz: number | bigint
  ): number => (ToNumber(offset) + ToNumber(sz) <= bytes.size ? 1 : 0)

  const p = instance.memory64 ? "j" : "i"
  const getBlockCb = instance.addCallback(getBlock, `i${p}${p}${p}${p}`)
  const isDataAvailCb = instance.addCallback(isDataAvail, `i${p}${p}${p}`)
  const free = () => {
    getBlockCb.free()
    isDataAvailCb.free()
  }

  try {
    const get_block = getBlockCb.pointer as unknown as GetBlockPointer
    const handle = AutoThrow(
      await (complete
        ? api.open_custom(size as Bytes, get_block)
        : api.open(
            size as Bytes,
            get_block,
            isDataAvailCb.pointer as unknown as IsDataAvailPointer
          ))
    )
    return {
      handle,
      close: async () => {
        AutoThrow(await api.close(handle))
        free()
      },
    }
  } catch (error) {
    free()
    throw error
  }
}

// the meta as written, for shared_update to read
type Shared = { meta: Uint8Array; content: BlobArrayType }

// The shared section, when the file starts with one (see StreamFormat): kept
// for every page, which needs it to be a whole pdf on its own.
const ReadShared = async (source: StreamBufferType): Promise<Shared | undefined> => {
  await source.pull(SECTION_PREFIX)
  const lengths = ParseSharedPrefix(
    await BlobToUint8(source.bytes.slice(0, Min(source.bytes.size, SECTION_PREFIX)))
  )
  if (NullQ(lengths)) return undefined
  await source.skip(SECTION_PREFIX)
  const meta = await source.take(lengths.meta)
  return { meta, content: await source.slice(lengths.section - SECTION_PREFIX - lengths.meta) }
}

// A section on its own: its marker blanked, and the shared objects appended
const Standalone = async (
  api: PdfiumAPI,
  prefix: Uint8Array,
  line: Uint8Array,
  body: BlobArrayType,
  shared: Shared | undefined
): Promise<BlobArrayType> => {
  const pdf = BlobArray([BlankMarker(prefix.length + line.length), body])
  if (!shared) return pdf
  const trailer = await BlobToUint8(pdf.slice(Max(0, pdf.size - MERGE_TRAILER_LENGTH), pdf.size))
  const update = AutoThrow(
    await api.shared_update(trailer, shared.meta, BigInt(pdf.size), BigInt(shared.content.size))
  )
  return BlobArray([pdf, shared.content, update])
}

// Our merge output (see StreamFormat): page after page, each section a pdf
// of its own whose first bytes give its length and its meta, handed over —
// its marker blanked, being on its own now, the shared objects appended — or
// opened by pdfium alone, and dropped once read. The first page's
// meta gives the count; pages come out in file order, and reading stops at the
// last one asked for — the rest is never downloaded.
async function* ReadPdfking<T>(
  api: PdfiumAPI,
  source: StreamBufferType,
  rangeFn: ((page_count: number) => Sequence<number>) | undefined,
  read: ReadPage<T>,
  section: ReadSection<T> | undefined
): AsyncGenerator<T> {
  const shared = await ReadShared(source)
  let page_count = POS_INFINITY
  let wanted: Set<number> | undefined
  for (let page = 1; page <= page_count && wanted?.size !== 0; page++) {
    // no marker is the end of the pages: what follows is the file's tail,
    // which no page needs, so the source is dropped there
    const prefix = await source.take(SECTION_PREFIX)
    const lengths = ParseSectionPrefix(prefix)
    if (NullQ(lengths)) return
    if (wanted && !wanted.has(page)) {
      await source.skip(lengths.section - SECTION_PREFIX)
      continue
    }

    const line = await source.take(lengths.meta)
    const body = await source.slice(lengths.section - SECTION_PREFIX - lengths.meta)
    const meta = ParseSectionMeta(line)
    if (!wanted) {
      page_count = meta.page_count
      wanted = new Set(PageRange(page_count, rangeFn))
    }
    if (!wanted.delete(page)) continue

    const pdf = await Standalone(api, prefix, line, body, shared)
    if (section) {
      yield section(pdf, meta, page)
      continue
    }

    const { handle, close } = await OpenPdfium(api, pdf, pdf.size, true)
    try {
      yield await read(handle, 0 as PageNumber, page)
    } finally {
      await close()
    }
  }
}

// Files up to this size are held whole before pdfium opens them: loading as
// from memory is the one way that also repairs a damaged file.
const COMPLETE_THRESHOLD = 31457280 as Bytes // 30 * 1024 * 1024

// Any other pdf: small ones are held whole and loaded as from memory, larger
// ones read as they arrive, pumping until each page pdfium asks for is in.
// Reading as it arrives takes knowing how long the file will be — pdfium is
// told before its first read — so a file nobody declared the length of (a
// chunked response, `size` 0) is held whole too, and is as long as what came.
async function* ReadDocument<T>(
  api: PdfiumAPI,
  source: StreamBufferType,
  size: Bytes,
  rangeFn: ((page_count: number) => Sequence<number>) | undefined,
  read: ReadPage<T>
): AsyncGenerator<T> {
  const complete = !size || size <= COMPLETE_THRESHOLD
  if (complete) await source.pull(POS_INFINITY)

  // the availability api can still answer "not yet" once every byte is in:
  // then the file is complete, and loads as such
  let opened = await OpenPdfium(api, source.bytes, complete ? source.bytes.size : size, complete)
  try {
    while (!AutoThrow(await api.doc_avail(opened.handle))) {
      if (!(await source.pull())) {
        await opened.close()
        opened = await OpenPdfium(api, source.bytes, source.bytes.size, true)
      }
    }

    for (const page of PageRange(ToNumber(await api.page_count(opened.handle)), rangeFn)) {
      const page_index = (page - 1) as PageNumber
      // nothing more to pull means the page is in, whatever pdfium says
      while (!AutoThrow(await api.page_avail(opened.handle, page_index))) {
        if (!(await source.pull())) break
      }
      yield await read(opened.handle, page_index, page)
    }
  } finally {
    await opened.close()
  }
}

// Reads the pages `rangeFn` asks for (all by default) out of a pdf as it
// streams in, handing each open page to `read`. The first bytes decide how:
// our own merge output is cut into its sections (see ReadPdfking), anything
// else goes to pdfium whole (see ReadDocument).
//
// Our own merge output starts with its integrity line (see StreamFormat), and
// is only trusted while it is what the merge wrote: the size the line records
// has to be the size of the file, unless it is 0 — nobody knew it. Sections
// are read front to back and the tail is never looked at, so content appended
// after the merge — an incremental save rewriting a page, a signature — would
// be silently ignored: a file that grew is read by pdfium as the plain pdf it
// also is.
export async function* ReadPages<T>(
  api: PdfiumAPI,
  stream: StreamInput,
  size: Bytes,
  rangeFn: ((page_count: number) => Sequence<number>) | undefined,
  read: ReadPage<T>,
  { chunk_size, section, skip_pdfking = false }: ReadPagesOptions<T>
): AsyncGenerator<T> {
  const source = StreamBuffer(stream, chunk_size)
  try {
    await source.pull(INTEGRITY_LENGTH)
    const { bytes } = source
    const recorded = ParseIntegrity(
      await BlobToUint8(bytes.slice(0, Min(bytes.size, INTEGRITY_LENGTH)))
    )
    const pdfking = !skip_pdfking && !NullQ(recorded) && (recorded === 0 || recorded === size)

    // the line is no part of any page
    if (pdfking) await source.skip(INTEGRITY_LENGTH)

    yield* pdfking
      ? ReadPdfking(api, source, rangeFn, read, section)
      : ReadDocument(api, source, size, rangeFn, read)
  } finally {
    await source.close()
  }
}
